import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { productAlertsApi } from '@/api';
import type { ProductAlertRequest, ProductAlertResponse } from '@/api/productAlerts.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ProductSelect } from '@/components/selects';
import { useProductOptions } from '@/hooks/useProductOptions';
import { useAppSelector } from '@/hooks/useAppSelector';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProductAlertsPage() {
  const store = useAppSelector((s) => s.app.selectedStore);
  const pagination = usePagination();
  const { getLabel: getProductLabel } = useProductOptions();
  const [alerts, setAlerts] = useState<ProductAlertResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<ProductAlertRequest>({
    storeId: 0,
    productId: 0,
    alertQuantity: 5,
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const rows = await productAlertsApi.list(store.id);
      setAlerts(rows);
      pagination.applyClientCount(rows.length);
    } catch (err) {
      showApiError(err, 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [store?.id, pagination]);

  useEffect(() => {
    if (store?.id) {
      setForm((f) => ({ ...f, storeId: store.id }));
      load();
    }
  }, [store?.id, load]);

  const columns: Column<ProductAlertResponse>[] = [
    { key: 'productName', header: 'Product', render: (r) => r.productName ?? getProductLabel(r.productId, true) },
    { key: 'sku', header: 'SKU' },
    {
      key: 'qty',
      header: 'Stock / Alert at',
      render: (r) => `${r.currentQuantity ?? '—'} / ${r.alertQuantity}`,
    },
    { key: 'active', header: 'Active', render: (r) => (r.active ? 'Yes' : 'No') },
    {
      key: 'last',
      header: 'Last notified',
      render: (r) => (r.lastAlertedAt ? new Date(r.lastAlertedAt).toLocaleString() : '—'),
    },
    {
      key: 'del',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-red-600"
          onClick={async () => {
            if (!confirm('Remove this alert?')) return;
            try {
              await productAlertsApi.delete(r.id);
              notify.success('Removed');
              load();
            } catch (err) {
              showApiError(err, 'Remove failed');
            }
          }}
        >
          Remove
        </button>
      ),
    },
  ];

  const save = async () => {
    if (!store?.id || !form.productId) {
      notify.error('Select a product');
      return;
    }
    setSaving(true);
    try {
      await productAlertsApi.save({ ...form, storeId: store.id });
      notify.success('Alert saved — you will get a notification when stock falls to this level');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!store) {
    return (
      <div>
        <PageHeader title="Product alerts" breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Alerts' }]} />
        <p className="text-slate-500">Select a store in the header to manage low-stock alerts.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Product alerts"
        description={`Low-stock thresholds for ${store.name}. Alerts appear in notifications and repeat at most once per 24 hours.`}
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Product alerts' }]}
        actions={
          <Button onClick={() => setModalOpen(true)}>
            <AddIcon fontSize="small" /> Add alert
          </Button>
        }
      />
      <DataTable
        columns={columns}
        data={paginateClientList(alerts, pagination.page, pagination.size)}
        keyField="id"
        loading={loading}
        {...dataTablePageProps(pagination)}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Low stock alert"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ProductSelect
            label="Product *"
            value={form.productId ? String(form.productId) : ''}
            onChange={(id) => setForm({ ...form, productId: id ? Number(id) : 0 })}
          />
          <Input
            label="Alert when quantity at or below *"
            type="number"
            value={String(form.alertQuantity)}
            onChange={(e) => setForm({ ...form, alertQuantity: Number(e.target.value) })}
          />
        </div>
      </Modal>
    </div>
  );
}
